import "../../../../.cache/static-styles-react/build-javascript/compiled.js";
/** @jsxRuntime automatic @jsxImportSource @terraindigital/static-styles-react */
import { cssRef as _cssRef, mergeComponentProps as _mergeComponentProps, px as _px, mergeExternalProps as _mergeExternalProps } from "@terraindigital/static-styles-react";
const _staticStyles = _cssRef("debug-provider__modalcontent-07rkwa", {
  maxWidth: "400px",
  maxHeight: "85vh",
  borderRadius: "8px"
});
const _staticStyles2 = _cssRef("debug-provider__modaltitle-30yqxk", {
  fontSize: "14px",
  fontWeight: "bold",
  marginBottom: "8px",
  borderBottom: "1px solid #eee",
  paddingBottom: "8px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
});
const _staticStyles3 = _cssRef("debug-provider__modalclosebutton-0lsiex", {
  backgroundColor: "#ef4444",
  color: "#fff",
  borderRadius: "4px",
  fontSize: "18px",
  fontWeight: "bold",
  transition: "0.2s",
  border: "none",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#dc2626",
    transform: "none"
  },
  "&:active": {
    transform: "scale(0.95)"
  }
});
const _staticStyles4 = _cssRef("debug-provider__debugpill-23bfoo", {
  backgroundColor: "var(--system-error)"
});
import React, { createContext, useContext, useEffect, useReducer, useMemo, Suspense } from 'react';
import DebugPill from './debug-pill';
import { colors } from '../colors';
import DraftDebug from './draft-debug';
import EnvironmentDebug from './environment-debug';
import PageSlugDebug from './page-slug';
import PropsBrowser from './props-browser';
import FranchiseNumberDebug from './franchise-number-debug';
import { Modal, ModalContent, ModalCloseButton, ModalTitle } from '../modal';
import { MicrositeContext } from '../../../contexts/franchise/microsite-context';
import { ENVIRONMENT } from '../../../constants';
const PageDebug = React.lazy(() => import('./page-debug'));
const GridLinesDebug = React.lazy(() => import('./grid-lines-debug'));
const CenterLineDebug = React.lazy(() => import('./center-line-debug'));
const FeatureFlagsDebug = React.lazy(() => import('./feature-flags-debug'));
interface DebugState {
  showBlockNames: boolean;
  showTemplateNames: boolean;
  showGridLines: boolean;
  showCenter: boolean;
  showCurrentPublishState: boolean;
  showPageSlug: boolean;
  showFeatureFlags: boolean;
  showFranchiseNumber: boolean;
  showEnvironmentInfo: boolean;
}
interface DebugContext extends DebugState {
  update: (key: keyof DebugState) => void;
}
const DebugContext = createContext<DebugContext | undefined>(undefined);
interface DebugProviderProps {
  children: React.ReactNode;
  id: string;
  pageProps: Omit<import('gatsby').PageProps, 'children'>;
  publishedAt?: string | null;
}
const DEBUG_LOCAL_KEY = 'servpro-debug';
const DEBUG_INITIAL_STATE: DebugState = {
  showBlockNames: false,
  showTemplateNames: false,
  showGridLines: false,
  showCenter: false,
  showCurrentPublishState: false,
  showPageSlug: false,
  showFeatureFlags: false,
  showFranchiseNumber: false,
  showEnvironmentInfo: false
};
const ITEMS_PER_PAGE = 3;
type DebugShowReducer = (state: DebugState, action: Partial<DebugState>) => DebugState;
const debugShowReducer: DebugShowReducer = (state, action) => {
  const update = {
    ...state,
    ...action
  };
  try {
    localStorage.setItem(DEBUG_LOCAL_KEY, JSON.stringify(update));
  } catch (e) {
    // ignore non-working localstorage
  }
  return update;
};
const DebugProvider = ({
  children,
  id,
  pageProps,
  publishedAt
}: DebugProviderProps): JSX.Element => {
  const shouldRenderWidget = !ENVIRONMENT.IS_SERVPRO_COM_TRUE_PRODUCTION;
  let pageSlug: string | null = null;
  if ('pageContext' in pageProps) {
    if ('slug' in pageProps.pageContext && typeof pageProps.pageContext.slug === 'string') {
      pageSlug = pageProps.pageContext.slug;
    }
  }

  // Track client-side mount to prevent SSR/hydration mismatch
  const [isMounted, setIsMounted] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [show, setShow] = useReducer<DebugShowReducer, Partial<DebugState>>(debugShowReducer, {}, () => DEBUG_INITIAL_STATE);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(0);
  const update = React.useCallback((key: keyof DebugState): void => {
    setShow({
      [key]: !show[key]
    });
  }, [show]);

  // Stable context value
  const contextValue = useMemo(() => ({
    ...show,
    update
  }), [show, update]);

  // Check if we're on a microsite
  const micrositeContext = React.useContext(MicrositeContext);
  const isMicrosite = micrositeContext && 'isMicrosite' in micrositeContext ? micrositeContext.isMicrosite : false;

  // Also check pageContext as fallback
  let isMicrositeFromContext = false;
  if (!isMicrosite && 'pageContext' in pageProps) {
    const context = pageProps.pageContext as {
      franchiseNumber?: number;
    };
    isMicrositeFromContext = !!context?.franchiseNumber;
  }
  const isOnMicrosite = isMicrosite || isMicrositeFromContext;

  // Check if any debug pills should be visible
  const hasVisibleDebugPills = show.showTemplateNames || show.showCurrentPublishState || show.showPageSlug || show.showEnvironmentInfo || show.showFranchiseNumber && isOnMicrosite;

  // Convert debug state to an array of items for filtering and pagination
  const debugItems = React.useMemo(() => Object.entries(show).map(([key, value]) => {
    const isFranchiseNumber = key === 'showFranchiseNumber';
    return {
      key: key as keyof DebugState,
      label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim(),
      value: value as boolean,
      disabled: isFranchiseNumber && !isOnMicrosite
    };
  }), [show, isOnMicrosite]);

  // Filter items based on a search query
  const filteredItems = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return debugItems;
    return debugItems.filter(f => {
      const keyMatch = f.key.toLowerCase().includes(q);
      const labelMatch = f.label.toLowerCase().includes(q);
      const statusMatch = (f.value ? 'enabled' : 'disabled').includes(q);
      return keyMatch || labelMatch || statusMatch;
    });
  }, [debugItems, searchQuery]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages - 1);

  // Adjust the current page if it's out of bounds after filtering
  React.useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);
  const paginatedItems = React.useMemo(() => {
    const start = safePage * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, safePage]);
  const handleSearch = (query: string): void => {
    setSearchQuery(query);
    setCurrentPage(0); // Reset to the first page when searching
  };

  // Set mounted the flag and load localStorage after client-side hydration
  useEffect(() => {
    setIsMounted(true);
    if (shouldRenderWidget) {
      try {
        const localDebug = localStorage.getItem(DEBUG_LOCAL_KEY);
        if (localDebug) {
          const parsed = JSON.parse(localDebug);
          if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
            setShow(parsed);
          }
        }
      } catch (e) {
        // ignore non-working localstorage
      }
    }
  }, [shouldRenderWidget]);
  return <DebugContext.Provider value={contextValue}>
            {/* Only render debug widgets after client mount to prevent hydration mismatch */}
            {shouldRenderWidget && isMounted && <>
                    <Modal open={open} onOpenChange={isOpen => {
        setOpen(isOpen);
        if (isOpen) {
          // Reset to first page when modal opens
          setCurrentPage(0);
        }
      }}>
                        <ModalContent {..._mergeComponentProps({
          css: _staticStyles
        })}>
                            <ModalTitle {..._mergeComponentProps({
            css: _staticStyles2
          })}>
                                <span>
                                    Layout Debug (
                                    {debugItems.length}
                                    )
                                    {filteredItems.length > 0 && <>
                                            {' '}
                                            <span className="debug-provider__span-0bb0hb">
                                                {safePage + 1}
                                                {' '}
                                                /
                                                {totalPages}
                                            </span>
                                        </>}
                                </span>
                            </ModalTitle>
                            <ModalCloseButton {..._mergeComponentProps({
            css: _staticStyles3
          })}>
                                ×
                            </ModalCloseButton>

                            {debugItems.length > 0 && <div className="debug-provider__div-03iecm">
                                    <input type="text" placeholder="Search options..." value={searchQuery} onChange={(e): void => handleSearch(e.target.value)} className="debug-provider__input-0ykp67" style={{
              "--static-styles-2gcl9k0r_paddingRight": _px(searchQuery ? '28px' : '8px')
            }} />
                                    {searchQuery && <button type="button" onClick={(): void => handleSearch('')} aria-label="Clear search" className="debug-provider__button-16rnc9">
                                            ×
                                        </button>}
                                </div>}

                            {debugItems.length > 0 && <>
                                    <div className="debug-provider__div-1cnla4">
                                        {paginatedItems.map((item, index) => {
                const isLast = index === paginatedItems.length - 1;
                return <div key={item.key} className="debug-provider__div-1inu48" style={{
                  "--static-styles-1wwfa4d7_borderBottom": _px(isLast && totalPages > 1 ? 'none' : '1px solid #eee')
                }}>
                                                    <div className="debug-provider__div-1j0ez0">
                                                        <span>{item.label}</span>
                                                    </div>

                                                    <div className="debug-provider__div-2r7ix2">
                                                        Current:
                                                        {' '}
                                                        {item.value ? 'Enabled' : 'Disabled'}
                                                    </div>

                                                    <div className="debug-provider__div-39c6of">
                                                        <button type="button" disabled={item.value || item.disabled} onClick={() => {
                      update(item.key);
                    }} className="debug-provider__button-28x23r" style={{
                      "--static-styles-1nkmsxdt_backgroundColor": _px(item.value ? '#28a745' : '#eee'),
                      "--static-styles-3b7wxx9r_color": _px((() => {
                        if (item.value) return 'white';
                        if (item.disabled) return '#999';
                        return 'black';
                      })()),
                      "--static-styles-0eysp6aq_cursor": _px(item.value || item.disabled ? 'not-allowed' : 'pointer'),
                      "--static-styles-3hphzs6o_opacity": item.value || item.disabled ? 1 : 0.8
                    }}>
                                                            Enable
                                                        </button>
                                                        <button type="button" disabled={!item.value || item.disabled} onClick={() => {
                      update(item.key);
                    }} className="debug-provider__button-02w3h4" style={{
                      "--static-styles-179l9ic8_backgroundColor": _px(!item.value ? '#dc3545' : '#eee'),
                      "--static-styles-0iquwbqa_color": _px(!item.value ? 'white' : '#999'),
                      "--static-styles-0usitemw_cursor": _px(!item.value || item.disabled ? 'not-allowed' : 'pointer'),
                      "--static-styles-2gr3gl59_opacity": !item.value || item.disabled ? 1 : 0.8
                    }}>
                                                            Disable
                                                        </button>
                                                    </div>
                                                </div>;
              })}
                                    </div>

                                    {filteredItems.length > ITEMS_PER_PAGE && <div className="debug-provider__div-2zi4r8">
                                            <button type="button" onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={safePage === 0} className="debug-provider__button-38b4xk" style={{
                "--static-styles-0l48jjka_backgroundColor": _px(safePage === 0 ? '#eee' : '#666'),
                "--static-styles-148arzpk_color": _px(safePage === 0 ? '#999' : 'white'),
                "--static-styles-0qz0ejo9_cursor": _px(safePage === 0 ? 'not-allowed' : 'pointer'),
                "--static-styles-2hpmepoa_opacity": safePage === 0 ? 1 : 0.8
              }}>
                                                Previous
                                            </button>

                                            <button type="button" onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} className="debug-provider__button-1howl2" style={{
                "--static-styles-2asw3vvn_backgroundColor": _px(safePage >= totalPages - 1 ? '#eee' : '#666'),
                "--static-styles-2fl1z0fx_color": _px(safePage >= totalPages - 1 ? '#999' : 'white'),
                "--static-styles-047mfdno_cursor": _px(safePage >= totalPages - 1 ? 'not-allowed' : 'pointer'),
                "--static-styles-2ta5ekld_opacity": safePage >= totalPages - 1 ? 1 : 0.8
              }}>
                                                Next
                                            </button>
                                        </div>}
                                </>}
                        </ModalContent>
                    </Modal>

                    <div className="debug-provider__div-3rc6rn" style={{
        "--static-styles-1vowy82y_padding": _px(isCollapsed ? '8px' : '12px'),
        "--static-styles-1b8ap5q0_flexDirection": _px(isCollapsed ? 'row' : 'column'),
        "--static-styles-0yd8c8qc_padding": _px(isCollapsed ? '10px' : '14px')
      }}>
                        {!isCollapsed && <>
                                <button type="button" onClick={() => {
            setOpen(true);
          }} className="debug-provider__button-23pfb9">
                                    <DebugPill>Debug</DebugPill>
                                </button>
                                <button type="button" onClick={() => {
            update('showFeatureFlags');
          }} aria-label="Open feature flags" className="debug-provider__button-23pfb9">
                                    <DebugPill>
                                        Feature flags
                                    </DebugPill>
                                </button>
                                <div className="debug-provider__div-3ekg59">
                                    <PropsBrowser {...pageProps} />
                                </div>
                                {id && <a href={`/api/get-editing-url?id=${id}`} target="_blank" rel="noopener noreferrer" className="debug-provider__a-2xmzdi">
                                        <DebugPill {..._mergeComponentProps({
              css: _staticStyles4
            })}>
                                            Edit Page
                                        </DebugPill>
                                    </a>}
                            </>}
                        <button type="button" onClick={() => setIsCollapsed(!isCollapsed)} aria-label={isCollapsed ? 'Expand debug panel' : 'Collapse debug panel'} className="debug-provider__button-29ltu7">
                            <span className="debug-provider__span-0w788n" style={{
            "--static-styles-1qnti0uh_transform": _px(isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)')
          }}>
                                ▼
                            </span>
                        </button>
                    </div>
                </>}
            {/* Only render debug overlays after client mount to prevent hydration mismatch */}
            {isMounted && <Suspense fallback={null}>
                    {/* Debug pills at the top - in document flow to push content down */}
                    {/* Only render the container when at least one pill is visible */}
                    {hasVisibleDebugPills && <div className="debug-provider__div-0hum2c">
                            <PageDebug {..._mergeExternalProps(pageProps)} />
                            <EnvironmentDebug />
                            <DraftDebug publishedAt={publishedAt} />
                            {pageSlug && <PageSlugDebug slug={pageSlug} />}
                            <FranchiseNumberDebug pageProps={pageProps} />
                        </div>}
                    <GridLinesDebug />
                    <CenterLineDebug />
                    <FeatureFlagsDebug />
                </Suspense>}
            {children}
        </DebugContext.Provider>;
};
export default DebugProvider;
export const useDebug = (): DebugContext => {
  const ctx = useContext(DebugContext);
  if (!ctx) {
    throw new Error('useDebug is missing parent DebugProvider.');
  }
  return ctx;
};